import { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';
import { PRIMARY, TEXT_MUTED, TEXT_DARK } from '../legacy-ball/table-management/designTokens';
import { ContributorsCardView } from './ContributorsCardView';
import { ContributorsTableView } from './ContributorsTableView';

export interface Contributor {
  name: string;
  tilePurchase: boolean;
  article: boolean;
  adPurchase: boolean;
  reviewer: boolean;
}

interface ContributorsResponse {
  contributors: Contributor[];
  totalCount: number;
}

interface ContributorsSectionProps {
  editionId: string;
}

type ViewMode = 'card' | 'table';

export function ContributorsSection({ editionId }: ContributorsSectionProps) {
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function fetchContributors() {
      setLoading(true);
      setError('');
      try {
        const data = await apiClient<ContributorsResponse>(
          `/weekend-booklet/editions/${editionId}/contributors`,
        );
        if (!cancelled) {
          setContributors(data.contributors);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message =
            err && typeof err === 'object' && 'message' in err
              ? (err as { message: string }).message
              : 'Failed to load contributors';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchContributors();
    return () => {
      cancelled = true;
    };
  }, [editionId]);

  if (loading) {
    return <p style={{ color: TEXT_MUTED }}>Loading contributors...</p>;
  }

  if (error) {
    return <p style={{ color: '#d32f2f' }}>{error}</p>;
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <span style={{ color: TEXT_DARK, fontWeight: 600 }}>
          Contributors ({contributors.length})
        </span>

        {/* ViewToggle */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              fontSize: '0.9rem',
              color: viewMode === 'card' ? PRIMARY : TEXT_MUTED,
              borderBottom: viewMode === 'card' ? `2px solid ${PRIMARY}` : '2px solid transparent',
              fontWeight: viewMode === 'card' ? 600 : 400,
            }}
            aria-pressed={viewMode === 'card'}
            onClick={() => setViewMode('card')}
          >
            Card
          </button>
          <button
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              fontSize: '0.9rem',
              color: viewMode === 'table' ? PRIMARY : TEXT_MUTED,
              borderBottom: viewMode === 'table' ? `2px solid ${PRIMARY}` : '2px solid transparent',
              fontWeight: viewMode === 'table' ? 600 : 400,
            }}
            aria-pressed={viewMode === 'table'}
            onClick={() => setViewMode('table')}
          >
            Table
          </button>
        </div>
      </div>

      {contributors.length === 0 ? (
        <p style={{ color: TEXT_MUTED }}>No contributors available</p>
      ) : viewMode === 'card' ? (
        <ContributorsCardView contributors={contributors} />
      ) : (
        <ContributorsTableView contributors={contributors} />
      )}
    </div>
  );
}
